/*
  <sm-people-pool> — draws a grid of people from a bigger roster.

  The carousel does this too, but its version lives inside <sm-carousel> and
  only runs when the row scrolls. This is the same idea for a static grid: the
  cards rendered by Liquid are the fallback and the thing search engines read,
  and if the pool page can be reached they are replaced by a fresh random draw.

  It shares the carousel's sessionStorage cache, so a page carrying both only
  fetches the roster once.

  Everything fails soft: any problem at all and the rendered cards stay.
*/
if (!customElements.get('sm-people-pool')) {
  customElements.define(
    'sm-people-pool',
    class SmPeoplePool extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.sm-people');
        if (!this.track || !this.dataset.pool) return;

        this.originals = Array.from(this.track.children);
        this.want = parseInt(this.dataset.count, 10) || this.originals.length;
        if (!this.want) return;

        // Only go to the network once the grid is close to being seen.
        this.io = new IntersectionObserver(
          ([entry]) => {
            if (!entry.isIntersecting) return;
            this.io.disconnect();
            this.load();
          },
          { rootMargin: '300px' }
        );
        this.io.observe(this);
      }

      disconnectedCallback() {
        if (this.io) this.io.disconnect();
      }

      async load() {
        const key = `sm-pool-v1:${this.dataset.pool}`;
        let people = null;

        try {
          const cached = sessionStorage.getItem(key);
          if (cached) people = JSON.parse(cached);
        } catch (e) {
          /* private mode, or storage disabled */
        }

        if (!people) {
          try {
            const res = await fetch(this.dataset.pool, { credentials: 'same-origin' });
            if (!res.ok) return;
            const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
            people = SmPeoplePool.readCards(doc);
            try {
              sessionStorage.setItem(key, JSON.stringify(people));
            } catch (e) {
              /* over quota. The draw still works. */
            }
          } catch (e) {
            return;
          }
        }

        const chosen = this.draw(people);
        if (chosen.length < this.want) return; // not enough to fill the grid
        this.apply(chosen);
      }

      /* Matches the markup on the index page, and the carousel's reader, so the
         one cached roster serves both. */
      static readCards(doc) {
        return Array.from(doc.querySelectorAll('.highlight-card'))
          .map((card) => {
            const img = card.querySelector('img');
            return {
              href: card.getAttribute('href'),
              name: card.querySelector('.person-card__name')?.textContent.trim(),
              meta: card.querySelector('.person-card__meta')?.textContent.trim() || '',
              src: img?.getAttribute('src'),
              srcset: img?.getAttribute('srcset') || '',
              alt: img?.getAttribute('alt') || '',
            };
          })
          .filter((p) => p.href && p.name && p.src);
      }

      /* A plain shuffle rather than the carousel's balanced draw. Four slots
         cannot hold one of every kind of guest, so balancing would just bias
         which kinds appear at all; random across the whole roster gives more
         variety from page to page. The person whose page this is never
         appears in their own list. */
      draw(people) {
        const here = (this.dataset.exclude || '').replace(/\/$/, '');
        const pool = people.filter((p) => p.href.replace(/\/$/, '') !== here);
        for (let i = pool.length - 1; i > 0; i -= 1) {
          const j = Math.floor(Math.random() * (i + 1));
          [pool[i], pool[j]] = [pool[j], pool[i]];
        }
        return pool.slice(0, this.want);
      }

      apply(people) {
        const template = this.originals[0];
        if (!template) return;

        const cards = people.map((p) => {
          const card = template.cloneNode(true);
          card.removeAttribute('aria-hidden');
          card.removeAttribute('tabindex');
          /* The effects layer may already have marked the template for scroll
             reveal. Cloning that hands the new card a hidden state with
             nothing watching to un-hide it. */
          card.removeAttribute('data-fx-reveal');
          card.classList.remove('fx-in');
          if (card.tagName === 'A') card.setAttribute('href', p.href);

          const name = card.querySelector('.person-card__name');
          if (name) name.textContent = p.name;

          const meta = card.querySelector('.person-card__meta');
          if (meta) meta.textContent = p.meta;

          const img = card.querySelector('img');
          if (img) {
            /* A <source> would win over the img it sits beside, so any art
               direction from the card we cloned has to go with it. */
            card.querySelectorAll('source').forEach((s) => s.remove());
            img.setAttribute('src', p.src);
            if (p.srcset) img.setAttribute('srcset', p.srcset);
            img.setAttribute('alt', p.alt || p.name);
            img.removeAttribute('width');
            img.removeAttribute('height');
          }
          return card;
        });

        this.track.replaceChildren(...cards);
        if (window.smFx) window.smFx.refresh();
      }
    }
  );
}
